import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  HostListener
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { TableComponent } from '../../Components/table/table.component';
import { SearchTaxAgentsComponent } from '../../Components/search-tax-agents/search-tax-agents.component';
import { Router } from '@angular/router';
import { ParaComponent } from '../../Components/para/para.component';
import { FilterPopoverComponent } from '../../Components/filter-popover/filter-popover.component';
import { EventEmitterService } from '../../Services/Common/event-emitter.service';
import { Subscription } from 'rxjs';
import { LoadJsonServiceService } from '../../Services/Common/load-json-service.service';
import { ApiService } from '../../Services/api';
import { AlertService } from '../../Services/notifications/alert.service';
import { LoggerService } from '../../Services/logger';

@Component({
  selector: 'app-manage-tax-agents',
  imports: [CommonModule, MatExpansionModule, FormsModule, MatIconModule, TableComponent, ParaComponent],
  templateUrl: './manage-tax-agents.component.html',
  styleUrl: './manage-tax-agents.component.scss'
})
export class ManageTaxAgentsComponent implements OnInit, OnChanges, OnDestroy {
  readonly searchTaxAgentsComponent = SearchTaxAgentsComponent;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private eventEmitterService: EventEmitterService,
    private cdr: ChangeDetectorRef,
    private httpService: LoadJsonServiceService,
    private alertService: AlertService,
    private logger: LoggerService
  ) { }
  @Input() data: any;
  selectedTabIndex: number = 0;
  searchText: string = '';
  tableData: any = null;
  authLevelDetails: any = null;
  dataSource: any[] = [];
  levelFilterOptions: string[] = [];
  selectedLevels: Set<string> = new Set();
  showLevelFilterPopover = false;
  @ViewChild('filterTrigger') filterTrigger?: ElementRef<HTMLElement>;
  private originalTaxAgentRows: any[] = [];
  private originalAuthorizationRows: any[] = [];
  private taxpayerConfig: any = null;
  private levelSelections = new Map<number, Set<string>>();
  private refreshSubscription: Subscription | null = null;
  public isLoadingData: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.data = changes['data'].currentValue;
      // this.logger.info('Data changed, refreshing...', undefined, 'manage-tax-agents.component.ts');
      this.callAPItoGetData();
    }
  }

  searchPrediction(e: any) {
    const input = (e?.target?.value || '').toString().toLowerCase();
    this.searchText = input;

    // Reset to originals when input is empty
    if (!input) {
      if (this.selectedTabIndex === 0) {
        this.tableData = this.tableData || this.getCurrentTableData();
        this.tableData.tableData = [...(this.originalTaxAgentRows || this.tableData.tableData || [])];
      } else if (this.selectedTabIndex === 1) {
        if (!this.authLevelDetails || !this.authLevelDetails.tableData) this.updateAuthLevelDetails();
        this.authLevelDetails.tableData.tableData = [...(this.originalAuthorizationRows || this.authLevelDetails.tableData.tableData || [])];
      }
      this.cdr.detectChanges();
      return;
    }

    // Filter from the cached originals so deleting characters broadens results
    const term = input;
    if (this.selectedTabIndex === 0) {
      const source = (Array.isArray(this.originalTaxAgentRows) && this.originalTaxAgentRows.length)
        ? this.originalTaxAgentRows
        : (this.getCurrentTableData()?.tableData || []);
      this.tableData = this.tableData || this.getCurrentTableData();
      this.tableData.tableData = source.filter((row: any) =>
        row.some((val: any) => val?.toString().toLowerCase().includes(term))
      );
    } else if (this.selectedTabIndex === 1) {
      const source = (Array.isArray(this.originalAuthorizationRows) && this.originalAuthorizationRows.length)
        ? this.originalAuthorizationRows
        : (this.authLevelDetails?.tableData?.tableData || []);
      if (!this.authLevelDetails || !this.authLevelDetails.tableData) this.updateAuthLevelDetails();
      this.authLevelDetails.tableData.tableData = source.filter((row: any) =>
        row.some((val: any) => val?.toString().toLowerCase().includes(term))
      );
    }
    this.cdr.detectChanges();
  }

  clearText() {
    // this.logger.info('clear Filter', undefined, 'manage-tax-agents.component.ts');
    this.searchText = '';
    this.tableData = this.getCurrentTableData();
    this.onFilterChange();
    this.onTableFiltersCleared();
  }

  onFilterChange() { }

  onTableFiltersCleared(): void {
    try {
      this.searchText = '';
      this.levelSelections.set(this.selectedTabIndex, new Set<string>());
      this.selectedLevels = this.getSelectionForTab(this.selectedTabIndex);

      if (this.selectedTabIndex === 0) {
        this.tableData = this.getCurrentTableData();
        this.tableData.tableData = [...(this.originalTaxAgentRows || this.tableData.tableData || [])];
      } else if (this.selectedTabIndex === 1) {
        if (!this.authLevelDetails || !this.authLevelDetails.tableData) this.updateAuthLevelDetails();
        this.authLevelDetails.tableData.tableData = [...(this.originalAuthorizationRows || this.authLevelDetails.tableData.tableData || [])];
      }
      this.cdr.detectChanges();
    } catch (err) {
      this.logger.warn('Failed to clear parent filters on child clear event', err, 'manage-tax-agents.component.ts');
    }
  }

  search() { }

  ngOnInit(): void {
    // this.logger.info('Manage Tax Agents Data', this.data, 'manage-tax-agents.component.ts');
    this.refreshSubscription = this.eventEmitterService.on().subscribe((event: string) => {
      if (event === 'refresh-manage-tax-agents') {
        this.refreshData();
      }
    });

    if (this.data) {
      this.initializeData();
    }

    this.updateLevelFilterOptionsForTab(this.selectedTabIndex);
    this.selectedLevels = this.getSelectionForTab(this.selectedTabIndex);
    this.loadLevelFilterOptions();
  }

  initializeData(): void {
    this.callAPItoGetData();
    this.tableData = this.getCurrentTableData();
    this.originalTaxAgentRows = Array.isArray(this.tableData?.tableData) ? [...this.tableData.tableData] : [];
    this.originalAuthorizationRows = Array.isArray(this.authLevelDetails?.tableData?.tableData)
      ? [...this.authLevelDetails.tableData.tableData]
      : [];
    this.authLevelDetails = {
      paragraphs: this.getAuthLevelParagraphs(),
      tableData: null
    };
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  refreshData(): void {
    if (this.isLoadingData) {
      // this.logger.info('Refresh already in progress, skipping duplicate refresh', undefined, 'manage-tax-agents.component.ts');
      return;
    }
    this.searchText = '';
    this.callAPItoGetData();
  }

  callAPItoGetData() {
    if (this.isLoadingData) {
      // this.logger.info('API call already in progress, skipping duplicate request', undefined, 'manage-tax-agents.component.ts');
      return;
    }

    this.isLoadingData = true;

    const url = (this.router && this.router.url && this.router.url.includes('/help'))
      ? '/help/getTickets'
      : (this.router && this.router.url && this.router.url.includes('/taxpayer'))
        ? '/taxpayer/taxagents'
        : '/taxpayer/taxagents';

    this.isLoadingData = true;

    this.apiService.get<any>(url).subscribe({
      next: (response) => {
        // setTimeout(() => {
        this.isLoadingData = false;
        //    this.cdr.markForCheck();
        //  }, 10000);
        if (response.success && response.data) {
          let responseData = response.data;
          this.logger.info('Data fetched from API', responseData, 'manage-tax-agents.component.ts');
          this.bindApiDataToTabs(responseData);
        }
      },
      error: (error) => {
        this.isLoadingData = false;
        this.cdr.markForCheck();
        this.logger.error('Error fetching data from API', error, 'manage-tax-agents.component.ts');
      }
    });
  }

  bindApiDataToTabs(responseData: any) {
    if (this.data && this.data[0] && this.data[0].tabs) {
      // Bind Tax Agents data (first tab)
      if (responseData.taxAgents && this.data[0].tabs[0]) {
        const taxAgentsTab = this.data[0].tabs[0];
        if (taxAgentsTab.content && taxAgentsTab.content.data && taxAgentsTab.content.data[0]) {
          taxAgentsTab.content.data[0].tableHeadings = responseData.taxAgents.tableHeadings;
          taxAgentsTab.content.data[0].tableData = responseData.taxAgents.tableData;
          this.originalTaxAgentRows = Array.isArray(responseData.taxAgents.tableData)
            ? [...responseData.taxAgents.tableData]
            : [];
        }
      } else {
        const taxAgentsTab = this.data[0].tabs[0];
        if (taxAgentsTab.content && taxAgentsTab.content.data && taxAgentsTab.content.data[0]) {
          taxAgentsTab.content.data[0].tableHeadings = responseData.tableHeadings;
          taxAgentsTab.content.data[0].tableData = responseData.tableData;
          taxAgentsTab.content.data[0].tableUrls = responseData.tableUrls;

          this.originalTaxAgentRows = Array.isArray(responseData.tableData)
            ? [...responseData.tableData]
            : [];
        }
        // this.logger.info('Data bound to Tax Agents tab from fallback', this.originalTaxAgentRows, 'manage-tax-agents.component.ts');
      }

      // Bind Authorization Level Details data (second tab)
      if (responseData.authorizationLevels && this.data[0].tabs[1]) {
        const authLevelTab = this.data[0].tabs[1];
        if (authLevelTab.content && authLevelTab.content.data && authLevelTab.content.data[0]) {
          authLevelTab.content.data[0].tableHeadings = responseData.authorizationLevels.tableHeadings;
          authLevelTab.content.data[0].tableData = responseData.authorizationLevels.tableData;
          this.originalAuthorizationRows = Array.isArray(responseData.authorizationLevels.tableData)
            ? [...responseData.authorizationLevels.tableData]
            : [];

          // Bind paragraphs if available in API response or fallback data
          if (responseData.authorizationLevels.paragraphs) {
            authLevelTab.content.data[0].paragraphs = responseData.authorizationLevels.paragraphs;
          }
        }
      }

      // this.logger.info('Data successfully bound to tabs', this.data, 'manage-tax-agents.component.ts');

      this.tableData = this.getCurrentTableData();
      if (this.selectedLevels.size > 0) {
        this.filterTaxAgentsByLevels(Array.from(this.selectedLevels));
      }
      if (this.selectedTabIndex === 1) {
        this.updateAuthLevelDetails();
      }
      this.updateLevelFilterOptionsForTab(this.selectedTabIndex);
      this.selectedLevels = this.getSelectionForTab(this.selectedTabIndex);
      this.cdr.markForCheck();
    }
  }

  handleButtonClick(button: any) {
    if (button.action) {
      const routeMatch = button.action.match(/routeToPage\('([^']+)'\)/);
      const route = routeMatch ? routeMatch[1] : null;

      if (route) {
        this.router.navigate([route]);
      } else {
        // this.logger.warn('Invalid route format', { details: button.action }, 'manage-tax-agents.component.ts');
      }
    }
  }

  selectTab(index: number, tabLabel: string): void {
    this.selectedTabIndex = index;
    // this.logger.info('Selected tab', { label: tabLabel, index }, 'manage-tax-agents.component.ts');
    this.tableData = this.getCurrentTableData();
    this.updateAuthLevelDetails();
    this.updateLevelFilterOptionsForTab(index);
    this.selectedLevels = this.getSelectionForTab(index);
    this.filterTaxAgentsByLevels(Array.from(this.selectedLevels));
  }

  getAuthLevelParagraphs(): string[] {

    if (this.data && this.data[0] && this.data[0].tabs && this.data[0].tabs[1]) {
      const authTab = this.data[0].tabs[1];
      if (authTab.content && authTab.content.data && authTab.content.data[0] && authTab.content.data[0].paragraphs) {
        return authTab.content.data[0].paragraphs;
      }
    }
    return [];
  }

  updateAuthLevelDetails() {
    const tabData = this.getCurrentTabData();
    if (tabData && tabData.tableHeadings && tabData.tableData) {
      this.authLevelDetails = {
        paragraphs: this.getAuthLevelParagraphs(),
        tableData: {
          tableType: tabData.tableType,
          tableHeadings: tabData.tableHeadings,
          tableData: tabData.tableData,
          tableUrls: tabData.tableUrls || []
        }
      };

      if (this.originalAuthorizationRows.length === 0 && Array.isArray(tabData.tableData)) {
        this.originalAuthorizationRows = [...tabData.tableData];
      }
    }
  }

  getTabs() {
    return this.data?.[0]?.tabs?.map((tab: any) => tab.name);
  }

  getCurrentTabData() {
    if (this.data && this.data[0] && this.data[0].tabs && this.data[0].tabs[this.selectedTabIndex]) {
      const currentTab = this.data[0].tabs[this.selectedTabIndex];
      if (currentTab.content && currentTab.content.data && currentTab.content.data[0]) {
        return currentTab.content.data[0];
      }
    }
    return null;
  }

  getCurrentTableData() {
    const tabData = this.getCurrentTabData();
    // this.logger.debug('Current Tab Data', tabData, 'manage-tax-agents.component.ts');
    if (tabData) {
      return {
        tableType: tabData.tableType,
        sortableFields: tabData.sortableFields || [],
        tableHeadings: tabData.tableHeadings || [],
        tableData: tabData.tableData || [],
        tableUrls: tabData.tableUrls || []
      };
    }
    return null;
  }

  // Clipboard / contextmenu / drop prevention handlers for local search input
  private _clipboardBlockTimeout: any = null;

  preventPaste(evt: ClipboardEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventCopy(evt: ClipboardEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventCut(evt: ClipboardEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventContext(evt: Event | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  preventDrop(evt: DragEvent | any) {
    try { evt.preventDefault(); } catch (e) { /* ignore */ }
    this.showBlockedClipboardMessage();
  }

  private showBlockedClipboardMessage() {
    if (this._clipboardBlockTimeout) return;
    this._clipboardBlockTimeout = setTimeout(() => { this._clipboardBlockTimeout = null; }, 1500);
    try {
      this.alertService.show("For security reasons this field does not allow copy or paste.", { type: 'info', duration: 3000 });
    } catch (e) { /* ignore */ }
  }

  routeToPage(route: string) {
    this.router.navigate([`/${route}`])
  }

  showAppointTax: boolean = false
  showContent() {
    this.showAppointTax = !this.showAppointTax

  }

  loadLevelFilterOptions(forceReload: boolean = false): void {
    const cacheBuster = forceReload ? `?t=${Date.now()}` : '';
    const jsonPath = `assets/JSONData/taxpayerService.json${cacheBuster}`;

    this.httpService.load(jsonPath).subscribe({
      next: (json:any) => {
         this.taxpayerConfig = json.response;
        this.levelFilterOptions = this.extractLevelOptions(json.response, this.selectedTabIndex);
      },
      error: (err) => {
        this.logger.error('Failed to load taxpayerService filter options', err, 'manage-tax-agents.component.ts');
        this.levelFilterOptions = [];
      }
    });
  }

  filterTaxAgentsByLevels(levels: string[]): void {
    console.log('Filtering by levels', levels);
    if (this.selectedTabIndex === 1) {
      const authConfig = this.authLevelDetails?.tableData;
      if (!authConfig) {
        return;
      }
      if (!Array.isArray(this.originalAuthorizationRows) || this.originalAuthorizationRows.length === 0) {
        this.originalAuthorizationRows = Array.isArray(authConfig.tableData) ? [...authConfig.tableData] : [];
      }
      const normalized = Array.isArray(levels)
        ? Array.from(new Set(levels.map(level => level?.trim()).filter(Boolean)))
        : [];
      if (!normalized.length) {
        authConfig.tableData = [...this.originalAuthorizationRows];
        this.cdr.detectChanges();
        return;
      }

      // perform case-insensitive matching by lowercasing normalized levels
      const normalizedLower = normalized.map(n => n.toString().toLowerCase());

      const levelColumnIndex = authConfig.tableHeadings.findIndex((heading: string) => (heading || '').toLowerCase().includes('level'));
      if (levelColumnIndex === -1) {
        return;
      }

      authConfig.tableData = this.originalAuthorizationRows.filter((row: any[]) => {
        const cell = row?.[levelColumnIndex];
        const rowLevels = this.normalizeLevelCell(cell) || [];
        return rowLevels.some(level => normalizedLower.includes((level || '').toString().toLowerCase()));
      });
      this.cdr.detectChanges();
      return;
    }

    const tableConfig = this.getTaxAgentsTabConfig();
    if (!tableConfig || !Array.isArray(tableConfig.tableData)) {
      return;
    }

    if (!Array.isArray(this.originalTaxAgentRows) || this.originalTaxAgentRows.length === 0) {
      this.originalTaxAgentRows = [...tableConfig.tableData];
    }

    const normalized = Array.isArray(levels)
      ? Array.from(new Set(levels.map(level => level?.trim()).filter(Boolean)))
      : [];

    if (normalized.length === 0) {
      tableConfig.tableData = [...this.originalTaxAgentRows];
      if (this.tableData) {
        this.tableData.tableData = [...this.originalTaxAgentRows];
      }
      return;
    }

    const levelColumnIndex = this.getAssignedLevelColumnIndex();
    if (levelColumnIndex === -1) {
      // this.logger.warn('Assigned Levels column not found for filtering', undefined, 'manage-tax-agents.component.ts');
      return;
    }

    const filteredRows = this.originalTaxAgentRows.filter(row => {
      const cell = row?.[levelColumnIndex];
      const rowLevels = this.normalizeLevelCell(cell);
      const normalizedLower = normalized.map(n => n.toString().toLowerCase());
      return rowLevels.some(level => normalizedLower.includes((level || '').toString().toLowerCase()));
    });

    tableConfig.tableData = filteredRows;
    if (this.tableData) {
      this.tableData.tableData = filteredRows;
    }
    this.cdr.detectChanges();
  }

  private extractLevelOptions(config: any, tabIndex: number): string[] {
    try {
      const taxAgentsSection = config?.menuContent?.taxAgents;
      if (!taxAgentsSection?.components) {
        return [];
      }

      const targetTabName = this.getTabs()?.[tabIndex];

      for (const group of taxAgentsSection.components) {
        if (!group?.components) continue;
        for (const component of group.components) {
          if (!component?.data) continue;
          for (const block of component.data) {
            const tabs = block?.tabs;
            if (!Array.isArray(tabs)) continue;

            for (const tab of tabs) {
              if (targetTabName && tab?.name !== targetTabName) {
                continue;
              }
              const entries = tab?.content?.data;
              if (!Array.isArray(entries)) continue;
              for (const entry of entries) {
                if (Array.isArray(entry?.filterOptions)) {
                  return this.normalizeLevelCell(entry.filterOptions).length
                    ? this.normalizeLevelCell(entry.filterOptions)
                    : [];
                }
              }
            }
          }
        }
      }
    } catch (err) {
      this.logger.error('Unable to parse taxpayerService filter options', err, 'manage-tax-agents.component.ts');
    }

    return [];
  }

  private getAssignedLevelColumnIndex(): number {
    const taxAgentHeadings = this.getTaxAgentsTabConfig()?.tableHeadings;
    const headings: string[] = taxAgentHeadings || this.tableData?.tableHeadings || [];
    return headings.findIndex(heading => (heading || '').toLowerCase().includes('assigned level'));
  }

  private normalizeLevelCell(value: any): string[] {
    if (Array.isArray(value)) {
      const cleaned = value.map(v => (v || '').toString().trim()).filter(Boolean);
      return Array.from(new Set(cleaned));
    }

    const asString = (value ?? '').toString();
    const cleaned = asString.split(',').map((part: string) => part.trim()).filter(Boolean);
    return Array.from(new Set(cleaned));
  }

  toggleLevelFilterPopover(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.levelFilterOptions.length) {
      this.loadLevelFilterOptions(true);
    }
    this.showLevelFilterPopover = !this.showLevelFilterPopover;
  }

  onLevelFilterApply(selection: Set<string>): void {
    const updated = new Set(selection);
    this.levelSelections.set(this.selectedTabIndex, updated);
    this.selectedLevels = updated;
    this.filterTaxAgentsByLevels(Array.from(this.selectedLevels));
    this.showLevelFilterPopover = false;
  }

  onLevelFilterReset(): void {
    const cleared = new Set<string>();
    this.levelSelections.set(this.selectedTabIndex, cleared);
    this.selectedLevels = cleared;
    this.filterTaxAgentsByLevels([]);
    this.showLevelFilterPopover = false;
  }

  closeLevelFilterPopover(): void {
    this.showLevelFilterPopover = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.showLevelFilterPopover) {
      return;
    }
    const clickedInside = this.filterTrigger?.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      this.showLevelFilterPopover = false;
    }
  }

  private getTaxAgentsTabConfig(): any | null {
    return this.data?.[0]?.tabs?.[0]?.content?.data?.[0] || null;
  }

  private getSelectionForTab(tabIndex: number): Set<string> {
    if (!this.levelSelections.has(tabIndex)) {
      this.levelSelections.set(tabIndex, new Set<string>());
    }
    return this.levelSelections.get(tabIndex)!;
  }

  private extractFilterOptionsFromInputData(tabIndex: number): string[] {
    const tab = this.data?.[0]?.tabs?.[tabIndex];
    if (!tab || !tab.content) {
      return [];
    }

    const dataBlocks = Array.isArray(tab.content.data) ? tab.content.data : [];

    for (const block of dataBlocks) {
      if (Array.isArray(block?.filterOptions)) {
        return this.normalizeLevelCell(block.filterOptions);
      }
      if (Array.isArray(block?.tabs)) {
        for (const innerTab of block.tabs) {
          const entries = Array.isArray(innerTab?.content?.data) ? innerTab.content.data : [];
          for (const entry of entries) {
            if (Array.isArray(entry?.filterOptions)) {
              return this.normalizeLevelCell(entry.filterOptions);
            }
          }
        }
      }
    }

    return [];
  }

  // Returns true when either the input data contains filterOptions for the current tab
  // or the loaded taxpayerConfig contains filterOptions for the current tab.
  public showFilterRow(): boolean {
    // If an explicit flag is present anywhere in the entire input `data` structure
    // for the current tab, let it take precedence. False hides, true shows.
    const activeTabName = this.getTabs()?.[this.selectedTabIndex || 0];
    if (this.data && Array.isArray(this.data)) {
      for (const top of this.data) {
        // search groups/components recursively
        const groups = Array.isArray(top?.components) ? top.components : [];
        for (const group of groups) {
          const result = this.findFlagByTabName(group, activeTabName);
          if (result !== null) return result;
        }
      }
    }

    // Fallback: if filter options exist in input data or in taxpayerConfig, show the row
    const fromInput = this.extractFilterOptionsFromInputData(this.selectedTabIndex || 0);
    if (fromInput && fromInput.length > 0) return true;
    if (this.taxpayerConfig) {
      const fromConfig = this.extractLevelOptions(this.taxpayerConfig, this.selectedTabIndex || 0);
      if (fromConfig && fromConfig.length > 0) return true;
    }
    return false;
  }

  // Search a group/component for the explicit flag for a specific tab name
  private findFlagByTabName(block: any, tabName: string | undefined): boolean | null {
    if (!block) return null;
    // Direct flag
    if (block.showFilterSearchRow === true) return true;
    if (block.showFilterSearchRow === false) return false;

    // If block has data with tabs, look for matching tab
    if (Array.isArray(block.data)) {
      for (const b of block.data) {
        if (Array.isArray(b.tabs)) {
          for (const t of b.tabs) {
            if (tabName && t?.name === tabName) {
              // look inside its content.data
              const res = this.findExplicitShowFlagInBlock(t?.content);
              if (res !== null) return res;
            }
            // also search deeper
            const deep = this.findFlagByTabName(t, tabName);
            if (deep !== null) return deep;
          }
        }
        const deeper = this.findFlagByTabName(b, tabName);
        if (deeper !== null) return deeper;
      }
    }

    // If block has components, scan them
    if (Array.isArray(block.components)) {
      for (const comp of block.components) {
        const res = this.findFlagByTabName(comp, tabName);
        if (res !== null) return res;
      }
    }

    return null;
  }

  // Recursively search a block for an explicit showFilterSearchRow flag.
  // Returns true, false, or null if not found.
  private findExplicitShowFlagInBlock(block: any): boolean | null {
    if (!block) return null;

    // Direct flag on block
    if (block.showFilterSearchRow === true) return true;
    if (block.showFilterSearchRow === false) return false;

    // If block contains data array, scan its items
    if (Array.isArray(block.content?.data)) {
      for (const child of block.content.data) {
        const result = this.findExplicitShowFlagInBlock(child);
        if (result !== null) return result;
      }
    }

    // If block directly has data array (top-level structure), scan it
    if (Array.isArray(block.data)) {
      for (const child of block.data) {
        const result = this.findExplicitShowFlagInBlock(child);
        if (result !== null) return result;
      }
    }

    // If block contains tabs, scan each tab's content
    if (Array.isArray(block.tabs)) {
      for (const t of block.tabs) {
        const result = this.findExplicitShowFlagInBlock(t?.content);
        if (result !== null) return result;
      }
    }

    return null;
  }

  private updateLevelFilterOptionsForTab(tabIndex: number): void {
    const fromInput = this.extractFilterOptionsFromInputData(tabIndex);
    if (fromInput.length) {
      this.levelFilterOptions = fromInput;
      return;
    }

    if (this.taxpayerConfig) {
      const fromConfig = this.extractLevelOptions(this.taxpayerConfig, tabIndex);
      if (fromConfig.length) {
        this.levelFilterOptions = fromConfig;
        return;
      }
    }

    this.levelFilterOptions = [];
  }

  // Keep one search experience per tab: Tax Agents uses the dedicated BIN search component.
  public shouldShowTopSearchRow(): boolean {
    if (this.selectedTabIndex === 0) {
      return false;
    }
    return this.showFilterRow();
  }

  public shouldShowTopActionButtons(): boolean {
    return this.selectedTabIndex !== 0 && (this.data?.[0]?.buttons?.length || 0) > 0;
  }
}

